import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBackIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Flex, HStack, IconButton, Stack, useDisclosure } from '@chakra-ui/react';
import { Page, RelationshipType, SystemTranslationKeys } from '@common/entities';
import { clearMobileCartOpenRequest } from 'state/slices/navigationSlice';
import { RootState } from 'state/store';

import { ShoppingCartIcon } from 'components/custom-icons';
import NotificationDrawer from 'components/notifications/NotificationDrawer';
import { useGetActiveStepIndex, useSystemTranslation } from 'hooks';
import { useVisiblePages } from 'hooks/display-logic/useVisiblePages';
import { AnyPage } from 'types/page';
import { hasDueNowPaymentRequired } from 'utils/charge-utils';
import { filterApplicationPages } from 'utils/filterApplicationPages';

import MobileMenuDrawer from './MobileMenuDrawer';
import MobileNavigation from './MobileNavigation';

interface Props {
  isNavigationLoading?: boolean;
  onNavigationStart?: () => void;
  onNavigationEnd?: () => void;
}

export default function MobileMenu({
  isNavigationLoading = false,
  onNavigationStart = () => undefined,
  onNavigationEnd = () => undefined,
}: Props) {
  const dispatch = useDispatch();
  const { tSystem } = useSystemTranslation();
  const { unit, leaseTerm } = useSelector((state: RootState) => state.unitSelection);
  const { isLeadProfile } = useSelector(
    (state: RootState) => state.auth ?? { isLeadProfile: false },
  );
  const applicationForm = useSelector((state: RootState) => state.application.form);
  const { charges, scheduledChargesLoaded } = useSelector((state: RootState) => state.shoppingCart);
  const { currencyCode } = useSelector((state: RootState) => state.locale);
  const mobileCartOpenRequested = useSelector(
    (state: RootState) => state.navigation.mobileCartOpenRequested ?? false,
  );
  const { pages } = applicationForm;

  const getActiveStepIndex = useGetActiveStepIndex();
  const activeStepIndex = getActiveStepIndex();

  // Filter pages (same logic as MobileNavigation)
  const filteredPages = useMemo(() => {
    const hasDueNowCharges = hasDueNowPaymentRequired(charges, currencyCode);
    return filterApplicationPages(
      applicationForm,
      RelationshipType.Primary,
      scheduledChargesLoaded ? hasDueNowCharges : undefined,
      isLeadProfile,
    );
  }, [applicationForm, charges, currencyCode, scheduledChargesLoaded, isLeadProfile]);

  // Get visible pages (excludes pages hidden by display logic)
  const { visiblePages, visiblePageCount } = useVisiblePages(filteredPages as AnyPage[]);

  // Find the current step index within the visible pages
  const currentPageId = pages[activeStepIndex]?.id;
  const displayStepIndex = useMemo(() => {
    return visiblePages.findIndex((p: Page) => p.id === currentPageId);
  }, [visiblePages, currentPageId]);

  const currentPageName = visiblePages[displayStepIndex]?.name;

  const handleFilteredPages = useCallback(() => {
    // No longer needed - we calculate locally now
  }, []);

  const {
    isOpen: isNavigationOpen,
    onOpen: onCartOpen,
    onClose: onNavigationClose,
  } = useDisclosure();
  const {
    isOpen: isMenuDrawerOpen,
    onOpen: onMenuDrawerOpen,
    onClose: onMenuDrawerClose,
  } = useDisclosure();
  const navigate = useNavigate();
  const { orgId, propertyId } = useParams();

  useEffect(() => {
    if (!mobileCartOpenRequested) return;

    onMenuDrawerOpen();
    dispatch(clearMobileCartOpenRequest());
  }, [dispatch, mobileCartOpenRequested, onMenuDrawerOpen]);

  const handleCartClick = () => {
    if (isNavigationOpen) {
      onNavigationClose();
    } else {
      onCartOpen();
    }
  };

  const handleMenuClick = () => {
    if (isMenuDrawerOpen) {
      onMenuDrawerClose();
    } else {
      onMenuDrawerOpen();
    }
  };

  return (
    <Stack flexBasis="100%" p={4} spacing={4}>
      <Flex justifyContent="space-between">
        <IconButton
          aria-label="Menu"
          bg="none"
          icon={<ArrowBackIcon boxSize={6} />}
          onClick={() => navigate(`/${orgId}/${propertyId}/entry`)}
        />
        <HStack>
          <NotificationDrawer />
          <Box position="relative">
            <IconButton
              onClick={handleMenuClick}
              aria-label={tSystem(SystemTranslationKeys.cart.shoppingCart)}
              bg="none"
              icon={<ShoppingCartIcon boxSize={6} />}
            />
            {unit && leaseTerm && (
              <Box
                data-testid="selected-home-indicator"
                position="absolute"
                top="7px"
                right="5px"
                width="12px"
                height="12px"
                bg="blue.500"
                borderRadius="full"
                border="2px solid white"
              />
            )}
          </Box>
        </HStack>
      </Flex>
      {displayStepIndex >= 0 && (
        <Flex justifyContent="space-between" onClick={handleCartClick} px={2}>
          <Box>{currentPageName}</Box>
          <Flex alignItems="center" fontSize="11px">
            {`Step ${displayStepIndex + 1} of ${visiblePageCount} `}
            <ChevronDownIcon ml={1} />
          </Flex>
        </Flex>
      )}
      <MobileNavigation
        isOpen={isNavigationOpen}
        onClose={onNavigationClose}
        onFilteredPages={handleFilteredPages}
        isNavigationLoading={isNavigationLoading}
        onNavigationStart={onNavigationStart}
        onNavigationEnd={onNavigationEnd}
      />
      <MobileMenuDrawer isOpen={isMenuDrawerOpen} onClose={onMenuDrawerClose} />
    </Stack>
  );
}
